import React, { useState, useEffect, useContext, useRef } from "react"
import StateContext from "../../context/state/StateContext"
import { getUserGroupUsers } from "../../helpers/DrupalHelper"
import FloatingQuantitySelect from "../Elements/Form/FloatingQuantitySelect"
import ButtonLite from "../Elements/ButtonLite"
import { userUsageForDisplay } from "../../helpers/allocationAvailability"

const UserAllocationPanel = ({
  segmentId,
  segmentAloc,
  totalStock,
  usageVisible = false,
  userAllocations,
  hasError = false,
  onChange,
}) => {
  const { token } = useContext(StateContext).state
  const [users, setUsers] = useState([])
  const [allocations, setAllocations] = useState([])
  const [loading, setLoading] = useState(true)
  // Track whether onChange has fired at least once to suppress false-positive red border on mount.
  const hasInteracted = useRef(false)

  useEffect(() => {
    let cancelled = false
    const fetchUsers = async () => {
      setLoading(true)
      try {
        const data = await getUserGroupUsers(token, segmentId)
        if (cancelled) return
        const segmentUsers = data?.users || []
        setUsers(segmentUsers)

        let result
        if (userAllocations?.length > 0) {
          // Remount with existing allocations — restore state without emitting to parent
          // to avoid overwriting unsaved edits in the parent's allocationState.
          const existingMap = new Map(
            userAllocations.map(a => [String(a.uid), a])
          )
          result = segmentUsers.map(u => {
            const existing = existingMap.get(String(u.uid))
            return {
              ...existing,
              uid: u.uid,
              name: u.name,
              aloc: existing?.aloc ?? 0,
              // Roster users with no grant paragraph have no consumption.
              // Seed ordered: 0 so a later onChange does not emit rows that
              // make sumRemainingGrants abort and freeze Available now on
              // the server's pre-edit figure (issue #379).
              ordered: existing?.ordered ?? 0,
            }
          })
        } else {
          result = segmentUsers.map(u => ({
            uid: u.uid,
            name: u.name,
            aloc: 0, // Always start at zero; brand uses "Set to equal split" if wanted.
            ordered: 0,
          }))
          // Emit only on first initialization so parent can validate pool remaining.
          hasInteracted.current = true
          onChange(result)
        }
        setAllocations(result)
      } catch (err) {
        if (!cancelled) {
          setUsers([])
          setAllocations([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (segmentId) fetchUsers()
    return () => {
      cancelled = true
    }
  }, [segmentId])

  const totalAssigned = allocations.reduce(
    (sum, a) => sum + (parseInt(a.aloc) || 0),
    0
  )
  const pool = segmentAloc - totalAssigned

  const updateUserAloc = (uid, newAloc) => {
    if (newAloc < 0) return
    const currentUser = allocations.find(a => a.uid === uid)
    if (!currentUser) return
    const delta = newAloc - currentUser.aloc
    if (delta > 0 && delta > pool) return

    const updated = allocations.map(a =>
      a.uid === uid ? { ...a, aloc: newAloc } : a
    )
    setAllocations(updated)
    hasInteracted.current = true
    onChange(updated)
  }

  const resetToEqual = () => {
    const perUser =
      allocations.length > 0 ? Math.floor(segmentAloc / allocations.length) : 0
    const reset = allocations.map(a => ({ ...a, aloc: perUser }))
    setAllocations(reset)
    hasInteracted.current = true
    onChange(reset)
  }

  const setToZero = () => {
    const zeroed = allocations.map(a => ({ ...a, aloc: 0 }))
    setAllocations(zeroed)
    hasInteracted.current = true
    onChange(zeroed)
  }

  if (loading) return <div className="p-4 text-reg-14">Loading users...</div>

  // Red border rules:
  // - hasError (parent says this segment is incomplete): always red, even on
  //   first render of a saved-but-unbalanced asset. This is the case that was
  //   previously invisible to the brand manager.
  // - Pool drift during live editing: red only after the brand interacts with
  //   the panel, to avoid a red flash while users are still being fetched.
  const showErrorStyle = hasError || (pool !== 0 && hasInteracted.current)

  return (
    <div
      className={`border rounded-[8px] p-4 mt-2 mb-2 ${
        showErrorStyle
          ? "border-red-400 bg-red-50"
          : "border-[#EBEBEB] bg-[#FAFAFA]"
      }`}
    >
      <div className="flex justify-between items-center mb-3">
        <span
          className={`text-reg-14 ${
            showErrorStyle ? "text-red-600" : "text-[#717171]"
          }`}
        >
          {allocations.length} users — Pool remaining: <strong>{pool}</strong>
        </span>
        <div className="flex space-x-3">
          <ButtonLite
            type="button"
            inline
            callback={resetToEqual}
            customContent={
              <span className="text-reg-11 text-[#2e669b] underline cursor-pointer">
                Set to equal split
              </span>
            }
          />
          <ButtonLite
            type="button"
            inline
            callback={setToZero}
            customContent={
              <span className="text-reg-11 text-[#2e669b] underline cursor-pointer">
                Set to zero
              </span>
            }
          />
        </div>
      </div>

      <div className="space-y-2">
        {allocations.map(a => {
          const usage =
            usageVisible === true ? userUsageForDisplay(a, totalStock) : null
          return (
            <div
              key={a.uid}
              className="flex items-center gap-4"
              data-testid={`user-allocation-${a.uid}`}
            >
              <span className="text-reg-14 w-[200px] shrink-0 truncate">
                {a.name}
              </span>
              <FloatingQuantitySelect
                flush
                count={a.aloc}
                label="Configured"
                decrement={() => updateUserAloc(a.uid, a.aloc - 1)}
                increment={() => updateUserAloc(a.uid, a.aloc + 1)}
                onChange={v => {
                  const val = parseInt(v)
                  if (!isNaN(val)) updateUserAloc(a.uid, val)
                }}
              />
              {usage && (
                <div
                  className="text-reg-11 text-[#555] min-w-0"
                  data-testid={`user-allocation-usage-${a.uid}`}
                >
                  {usage.enforced === false && (
                    <div className="text-[#9A6700] font-medium">
                      Not currently enforced at checkout
                    </div>
                  )}
                  <span>
                    Available now: <strong>{usage.availableNow}</strong>
                  </span>
                  <span className="mx-1">·</span>
                  <span>Remaining grant: {usage.remaining}</span>
                  <span className="mx-1">·</span>
                  <span>Ordered: {usage.ordered}</span>
                  {usage.exhausted && (
                    <span className="ml-2 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-red-700 font-semibold">
                      Exhausted
                    </span>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {(pool > 0 || (hasError && pool === 0)) && (
        <p className="text-reg-11 text-red-600 mt-2 font-medium">
          All {segmentAloc} units must be assigned to users before saving.
        </p>
      )}
      {pool < 0 && (
        <p className="text-reg-11 text-red-600 mt-2 font-medium">
          Over-allocated by {Math.abs(pool)} units. Reduce user allocations.
        </p>
      )}
    </div>
  )
}

export default UserAllocationPanel
